import React from 'react'
import {useCallback, useEffect, useState} from 'react'
import {Button, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useTelephony} from '../../hooks/useTelephony'
import {TELEPHONY_MESSAGES as T} from '../../helpers/telephonyMessages'
import {startIncomingRing} from './telephonyRingtone'
import {
  TELEPHONY_DRAWER_STATE_EVENT,
  TELEPHONY_DRAWER_TOGGLE_EVENT,
  TelephonyDrawerStateDetail,
} from '../../types/telephony'

// Manual campaign (0001) inbound-ring state — mirrors the drawer: the agent is parked
// in the INBND "waiting for calls" pause and a call is queued for them.
const MANUAL_CAMPAIGN_ID = '0001'
const MANUAL_INBOUND_PAUSE_CODE = 'INBND'

/* Keyframes de los botones flotantes. Es CSS, no texto de interfaz: vive fuera
   del JSX para no recrearlo en cada render ni disparar el rule de i18n. */
const FLOATING_TOOLS_KEYFRAMES = `
  @keyframes policytools-vicidial-pulse {
    0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55); transform: translateX(0); }
    50% { box-shadow: 0 0 0 12px rgba(34, 197, 94, 0); transform: translateX(-2px); }
    100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); transform: translateX(0); }
  }
  @keyframes policytools-vicidial-ring {
    0%, 49% { background-color: #dc2626; border-color: #dc2626; box-shadow: 0 0 0 8px rgba(220, 38, 38, 0.45); }
    50%, 100% { background-color: #f59e0b; border-color: #f59e0b; box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
  }
`

const floatingToolsStyle: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  right: 0,
  transform: 'translateY(-50%)',
  zIndex: 1051,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}

const floatingTabStyle: React.CSSProperties = {
  borderRadius: '10px 0 0 10px',
  padding: '8px 12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
}

const incomingPulseStyle: React.CSSProperties = {
  backgroundColor: '#16a34a',
  borderColor: '#16a34a',
  color: '#fff',
  animation: 'policytools-vicidial-pulse 1s ease-in-out infinite',
}

const activeCallStyle: React.CSSProperties = {
  backgroundColor: '#15803d',
  borderColor: '#15803d',
  color: '#fff',
}

// A waiting inbound call: blink hard so the agent notices even when not looking here.
const ringingPulseStyle: React.CSSProperties = {
  color: '#fff',
  animation: 'policytools-vicidial-ring 0.6s steps(1) infinite',
}

const hasMeaningfulVicidialValue = (value?: string) => {
  const normalized = String(value || '').trim().toUpperCase()
  return Boolean(normalized && !['0', 'NONE', 'NULL', 'N/A'].includes(normalized))
}

const buildCallerLabel = (firstName?: string, lastName?: string, phoneNumber?: string, vendorLeadCode?: string) => {
  const displayName = [String(firstName || '').trim(), String(lastName || '').trim()]
    .filter((value) => hasMeaningfulVicidialValue(value))
    .join(' ')

  return String(displayName || phoneNumber || vendorLeadCode || '').trim()
}

const isDispositionPendingState = (status?: string, realTimeSubStatus?: string) => {
  const statusValue = String(status || '').trim().toUpperCase()
  const subStatusValue = String(realTimeSubStatus || '').trim().toUpperCase()
  return (
    statusValue === 'DEAD' ||
    subStatusValue === 'DEAD' ||
    subStatusValue.includes('DEAD') ||
    statusValue === 'DISPO' ||
    subStatusValue === 'DISPO'
  )
}

export const TelephonyFloatingButton: React.FC = () => {
  const intl = useIntl()
  const telephony = useTelephony()
  const [drawerState, setDrawerState] = useState<TelephonyDrawerStateDetail>({
    mounted: false,
    visible: false,
    provider: undefined,
  })
  const tooltipLabel = intl.formatMessage(
    drawerState.mounted && drawerState.provider === 'VICIDIAL'
      ? drawerState.visible
        ? T.hideWebphone
        : T.showWebphone
      : T.openWebphone
  )

  const vicidialStatus = String(telephony.vicidialSession.status || '').trim().toUpperCase()
  const vicidialSubStatus = String(telephony.vicidialSession.realTimeSubStatus || '').trim().toUpperCase()
  const isPausedState = vicidialStatus === 'PAUSED'
  const dispositionPending = isDispositionPendingState(vicidialStatus, vicidialSubStatus)
  const hasCallContext =
    !dispositionPending &&
    (hasMeaningfulVicidialValue(telephony.vicidialSession.callId) ||
      hasMeaningfulVicidialValue(telephony.vicidialSession.leadId))
  const hasIncomingLeadContext =
    !dispositionPending &&
    !isPausedState &&
    hasMeaningfulVicidialValue(telephony.vicidialSession.phoneNumber) &&
    hasMeaningfulVicidialValue(telephony.vicidialSession.vendorLeadCode)
  const isInCall =
    !dispositionPending &&
    (
      vicidialStatus === 'INCALL' ||
      vicidialSubStatus.includes('INCALL') ||
      vicidialSubStatus.includes('LIVE')
    )
  const isIncomingAlert =
    !drawerState.visible &&
    telephony.vicidialSession.activeSession &&
    !isPausedState &&
    (hasIncomingLeadContext || hasCallContext) &&
    !isInCall

  const callerLabel = buildCallerLabel(
    telephony.vicidialSession.firstName,
    telephony.vicidialSession.lastName,
    telephony.vicidialSession.phoneNumber,
    telephony.vicidialSession.vendorLeadCode
  )

  const effectiveTooltipLabel = isIncomingAlert
    ? callerLabel
      ? intl.formatMessage(T.incomingCallFrom, {caller: callerLabel})
      : intl.formatMessage(T.notificationIncoming)
    : isInCall
    ? callerLabel
      ? intl.formatMessage(T.callActiveWith, {caller: callerLabel})
      : intl.formatMessage(T.callActive)
    : tooltipLabel

  const isRingingInbound =
    String(telephony.vicidialSession.campaignId || '') === MANUAL_CAMPAIGN_ID &&
    isPausedState &&
    String(telephony.vicidialSession.pauseCode || '').trim().toUpperCase() === MANUAL_INBOUND_PAUSE_CODE &&
    (Number(telephony.vicidialSession.pendingInboundCount) || 0) > 0

  const floatingButtonStyle: React.CSSProperties = {
    ...floatingTabStyle,
    ...(isRingingInbound
      ? ringingPulseStyle
      : isIncomingAlert
      ? incomingPulseStyle
      : isInCall
      ? activeCallStyle
      : {}),
  }

  useEffect(() => {
    const handleDrawerState = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyDrawerStateDetail>
      if (!customEvent.detail) return
      setDrawerState(customEvent.detail)
    }

    window.addEventListener(TELEPHONY_DRAWER_STATE_EVENT, handleDrawerState as EventListener)

    return () => {
      window.removeEventListener(TELEPHONY_DRAWER_STATE_EVENT, handleDrawerState as EventListener)
    }
  }, [])

  // Alert to a waiting inbound call even when the agent isn't looking at the drawer
  // or is on another tab: loud phone ring + tab-title flash + desktop notification.
  useEffect(() => {
    if (!isRingingInbound) return
    const stopRing = startIncomingRing()
    const originalTitle = document.title
    let flip = false
    const titleTimer = window.setInterval(() => {
      flip = !flip
      document.title = flip ? intl.formatMessage(T.tabIncoming) : originalTitle
    }, 1000)
    let notif: Notification | null = null
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        notif = new Notification(intl.formatMessage(T.notificationIncoming), {
          body: intl.formatMessage(T.notificationBody),
          tag: 'policydial-incoming',
          requireInteraction: true,
        })
        notif.onclick = () => {
          try {
            window.focus()
          } catch {
            /* ignore */
          }
          window.dispatchEvent(new CustomEvent(TELEPHONY_DRAWER_TOGGLE_EVENT))
          try {
            notif?.close()
          } catch {
            /* ignore */
          }
        }
      }
    } catch {
      /* notifications unavailable — ring + title flash still alert the agent */
    }
    return () => {
      stopRing()
      window.clearInterval(titleTimer)
      document.title = originalTitle
      try {
        notif?.close()
      } catch {
        /* ignore */
      }
    }
  }, [isRingingInbound])

  // Ask for desktop-notification permission once (best-effort; also requested on the
  // first webphone-open click, which is a user gesture some browsers require).
  useEffect(() => {
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {})
      }
    } catch {
      /* ignore */
    }
  }, [])

  const handleOpenVicidialWebphone = useCallback(async () => {
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        void Notification.requestPermission()
      }
    } catch {
      /* ignore */
    }
    if (drawerState.mounted && drawerState.provider === 'VICIDIAL') {
      window.dispatchEvent(new CustomEvent(TELEPHONY_DRAWER_TOGGLE_EVENT))
      return
    }

    if (telephony.vicidialSession.activeSession) {
      await telephony.openWebphone('VICIDIAL', {preferAgentSession: true})
      return
    }

    if (telephony.vicidialSession.connected || telephony.vicidialSession.connecting) {
      await telephony.openWebphone('VICIDIAL', {preferAgentSession: true})
      return
    }

    await telephony.connectVicidialSession()
  }, [drawerState.mounted, drawerState.provider, telephony])

  if (!telephony.config.vicidial_enabled) {
    return null
  }

  return (
    <div style={floatingToolsStyle} id="global-telephony-floating-tools">
      <style>{FLOATING_TOOLS_KEYFRAMES}</style>
      <OverlayTrigger
        placement='left'
        overlay={<Tooltip id='telephony-vicidial-webphone-tooltip'>{effectiveTooltipLabel}</Tooltip>}
      >
        <Button
          style={floatingButtonStyle}
          variant={
            isRingingInbound
              ? 'danger'
              : isIncomingAlert || isInCall
              ? 'success'
              : drawerState.mounted && drawerState.visible
              ? 'dark'
              : telephony.vicidialSession.connected
              ? 'primary'
              : 'light'
          }
          title={
            isRingingInbound
              ? intl.formatMessage(T.incomingClickToAnswer)
              : effectiveTooltipLabel
          }
          onClick={handleOpenVicidialWebphone}
          disabled={!telephony.vicidialSession.connected && !telephony.vicidialSession.connecting}
        >
          <i className={`bi ${isRingingInbound ? 'bi-telephone-inbound-fill' : 'bi-telephone'} fs-4`} />
        </Button>
      </OverlayTrigger>
    </div>
  )
}
